/**
 * R5's "`Approve all` behind a count-naming confirm when there is more than
 * one." This is a CONSTRUCTIVE action, so it deliberately does not use
 * `confirmDestructive`, whose host renders `variant="destructive"`
 * unconditionally at
 * `packages/ui/src/confirmDestructive/ConfirmDestructiveHost.tsx:86`.
 * Approving people into a pool is not destructive.
 */
import { Button, Dialog } from "@sportsmet/ui";
import { type ReactNode } from "react";

export const APPROVE_ALL_DIALOG_NAME = "approve-all";
export const APPROVE_ALL_BODY = "Everyone waiting joins the pool right now.";
export const APPROVE_ALL_CONFIRM_LABEL = "Approve all";
export const APPROVE_ALL_CANCEL_LABEL = "Cancel";

export function APPROVE_ALL_TITLE(count: number): string {
  if (count < 2) {
    throw new Error("Approve all needs more than one request");
  }
  return `Approve ${count} requests?`;
}

export type typeApproveAllDialogProps = {
  open: boolean;
  count: number;
  isBusy: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
};

export const ApproveAllDialog = ({
  open,
  count,
  isBusy,
  onOpenChange,
  onConfirm,
}: typeApproveAllDialogProps): ReactNode => {
  if (open && count < 2) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={count < 2 ? "" : APPROVE_ALL_TITLE(count)}
      autoFocus={false}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {APPROVE_ALL_CANCEL_LABEL}
          </Button>
          <Button
            variant="primary"
            loading={isBusy}
            disabled={isBusy}
            onClick={onConfirm}
          >
            {APPROVE_ALL_CONFIRM_LABEL}
          </Button>
        </>
      }
    >
      <p className="text-body text-ink">{APPROVE_ALL_BODY}</p>
    </Dialog>
  );
};
